import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/providers/AuthProvider';
import { useLanguage } from '@/contexts/LanguageContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, Mail, Phone, User, Lock, Globe, ArrowRight, Gift } from 'lucide-react';
import { useSentry } from '@/hooks/useSentry';
import { isRideDriverEmail } from '@/config/features';

const AuthPage = () => {
  const [activeTab, setActiveTab] = useState<'login' | 'signup'>('login');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [useEmail, setUseEmail] = useState(false);
  const { toast } = useToast();
  const { user, loading: authLoading } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { captureError, captureMessage } = useSentry();

  // Check for password reset flow or redirect authenticated users
  useEffect(() => {
    // Check URL hash for recovery type (Supabase sends tokens via hash fragments)
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const typeFromHash = hashParams.get('type');
    const typeFromQuery = searchParams.get('type');
    
    if (typeFromHash === 'recovery' || typeFromQuery === 'recovery') {
      setShowPasswordReset(true);
    } else if (user && !showPasswordReset) {
      // Check for redirect parameter (e.g., from checkout or shared vendor link)
      const redirectTo = searchParams.get('redirect');
      if (redirectTo) {
        navigate(redirectTo);
      } else if (isRideDriverEmail(user.email)) {
        navigate('/ride-driver-dashboard');
      } else {
        // Check if user is a delivery driver
        supabase
          .from('driver_profiles')
          .select('id')
          .eq('user_id', user.id)
          .maybeSingle()
          .then(({ data }) => {
            if (data) {
              navigate('/driver-dashboard');
            } else {
              navigate('/home');
            }
          });
      }
    }
  }, [user, navigate, searchParams]);

  // Listen for PASSWORD_RECOVERY event from Supabase auth
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        setShowPasswordReset(true);
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  const [showReferralField, setShowReferralField] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    password: '',
    newPassword: '',
    confirmPassword: '',
    referralCode: ''
  });

  const handleSignUp = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Ensure phone number has proper format with country code
      const phoneWithCountryCode = formData.phone.startsWith('+220') 
        ? formData.phone 
        : `+220${formData.phone.replace(/^\+?220?/, '')}`;

      console.log('Attempting signup with:', {
        email: formData.email,
        phone: phoneWithCountryCode,
        fullName: formData.fullName
      });

      const { data, error } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth`,
          data: {
            full_name: formData.fullName,
            phone: phoneWithCountryCode,
          },
        },
      });

      if (error) {
        console.error('Signup error:', error);
        
        // Report to Sentry with context
        captureError(error, {
          page: 'auth',
          action: 'signup',
          email: formData.email,
          phone: phoneWithCountryCode,
          errorType: 'supabase_signup_error'
        });
        
        // Check for various duplicate account scenarios
        if (error.message.includes('already registered') || 
            error.message.includes('User already registered') ||
            error.message.includes('already been registered')) {
          toast({
            title: "Account Already Exists",
            description: "An account with this email already exists. Please try logging in instead.",
            variant: "destructive",
          });
          setActiveTab('login');
        } else {
          toast({
            title: "Error",
            description: error.message,
            variant: "destructive",
          });
        }
      } else if (data?.user) {
        console.log('Signup successful:', data);
        
        // Handle referral code if provided
        if (formData.referralCode.trim() && data.user.id) {
          setTimeout(async () => {
            try {
              const { data: refData } = await supabase.rpc('handle_referral_signup', {
                p_new_user_id: data.user!.id,
                p_referral_code: formData.referralCode.trim()
              });
              const refResult = refData as { success: boolean } | null;
              if (refResult?.success) {
                toast({
                  title: "🎉 Referral code accepted!",
                  description: "You'll get D50 off your first order automatically.",
                });
              }
            } catch (refError) {
              console.log('Referral handling failed (non-blocking):', refError);
            }
          }, 2000); // wait for profile to be created by trigger
        }
        
        // Send welcome email asynchronously (don't block signup)
        setTimeout(async () => {
          try {
            await supabase.functions.invoke('send-welcome-email', {
              body: {
                email: formData.email,
                fullName: formData.fullName
              }
            });
            console.log('Welcome email sent successfully');
          } catch (emailError) {
            console.log('Welcome email failed (non-blocking):', emailError);
          }
        }, 100);
        
        toast({
          title: "Welcome to GAMBGO! 🎉",
          description: "Please check your email to confirm your account and claim your welcome offer!",
        });
      }
    } catch (error) {
      console.error('Unexpected signup error:', error);
      
      // Report unexpected errors to Sentry
      captureError(error as Error, {
        page: 'auth',
        action: 'signup',
        email: formData.email,
        errorType: 'unexpected_signup_error'
      });
      
      toast({
        title: "Error",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    }
    setLoading(false);
  };

  const handleSignIn = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    try {
      let emailForAuth = formData.email;

      // If using phone number, look up the email first
      if (!useEmail && formData.phone) {
        let phoneWithCountryCode = formData.phone.trim();
        
        // Normalize phone number format
        if (!phoneWithCountryCode.startsWith('+220')) {
          // Remove any existing country code or plus sign
          phoneWithCountryCode = phoneWithCountryCode.replace(/^\+?220?/, '');
          phoneWithCountryCode = `+220${phoneWithCountryCode}`;
        }
        
        console.log('Looking up phone:', phoneWithCountryCode);
        
        // Try multiple phone format variations
        const phoneVariations = [
          phoneWithCountryCode,
          phoneWithCountryCode.replace('+220', '220'),
          phoneWithCountryCode.replace('+220', ''),
          formData.phone // original input
        ];
        
        let profileData = null;
        let profileError = null;
        
        // Try each phone variation
        for (const phoneVariation of phoneVariations) {
          const { data, error } = await supabase
            .from('profiles')
            .select('email, id')
            .eq('phone', phoneVariation)
            .maybeSingle();
          
          if (!error && data) {
            profileData = data;
            break;
          }
          profileError = error;
        }

        console.log('Profile lookup result:', { profileData, profileError });

        if (!profileData) {
          toast({
            title: "Phone Number Not Found",
            description: `No account found with phone number ${phoneWithCountryCode}. Please sign up first or use email to login.`,
            variant: "destructive",
          });
          setLoading(false);
          return;
        }

        emailForAuth = profileData.email;
        console.log('Found email for phone:', emailForAuth);
      }

      console.log('Authenticating with email:', emailForAuth);

      // Now authenticate with email and password
      const { error } = await supabase.auth.signInWithPassword({
        email: emailForAuth,
        password: formData.password,
      });

      if (error) {
        console.error('Sign in error:', error);
        
        // Report sign-in errors to Sentry
        captureError(error, {
          page: 'auth',
          action: 'signin',
          useEmail,
          email: useEmail ? formData.email : emailForAuth,
          errorType: 'supabase_signin_error'
        });
        
        if (error.message.includes('Invalid login credentials')) {
          toast({
            title: "Login Failed",
            description: "Invalid phone number or password. Please check your credentials and try again.",
            variant: "destructive",
          });
        } else {
          toast({
            title: "Error",
            description: error.message,
            variant: "destructive",
          });
        }
      } else {
        toast({
          title: "Welcome back!",
          description: "Successfully logged in.",
        });
      }
    } catch (error) {
      console.error('Unexpected error:', error);
      
      // Report unexpected sign-in errors to Sentry
      captureError(error as Error, {
        page: 'auth',
        action: 'signin',
        useEmail,
        errorType: 'unexpected_signin_error'
      });
      
      toast({
        title: "Error",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    }
    setLoading(false);
  };

  const handleForgotPassword = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    const { error } = await supabase.auth.resetPasswordForEmail(formData.email, {
      redirectTo: `${window.location.origin}/auth`,
    });

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Success",
        description: "Password reset email sent. Check your inbox.",
      });
      setShowForgotPassword(false);
    }
    setLoading(false);
  };

  const handlePasswordReset = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);

    if (formData.newPassword !== formData.confirmPassword) {
      toast({
        title: "Error",
        description: "Passwords don't match.",
        variant: "destructive",
      });
      setLoading(false);
      return;
    }

    const { error } = await supabase.auth.updateUser({
      password: formData.newPassword
    });

    if (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: "Success",
        description: "Password updated successfully!",
      });
      navigate('/settings/edit-profile');
    }
    setLoading(false);
  };

  const handleBrowseAsGuest = () => {
    navigate('/home');
  };

  const languageOptions = [
    { value: 'en', label: 'English' },
    { value: 'fr', label: 'Français' },
    { value: 'de', label: 'Deutsch' },
    { value: 'es', label: 'Español' },
    { value: 'pt', label: 'Português' }
  ];

  if (showPasswordReset) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-orange-50 to-yellow-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 opacity-30" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ff6b6b' fill-opacity='0.03'%3E%3Cpath d='m36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}></div>
        
        <Card className="w-full max-w-md relative z-10 shadow-2xl border-0 bg-white/90 backdrop-blur-sm">
          <CardHeader className="text-center space-y-4 pb-8">
            <div className="mx-auto w-20 h-20 rounded-full flex items-center justify-center mb-4 overflow-hidden bg-white shadow-lg ring-4 ring-red-100">
              <img 
                src="/lovable-uploads/917e51ff-8660-4c82-b344-b93189a34ba9.png" 
                alt="GAMBGO Logo" 
                className="w-16 h-16 object-contain"
              />
            </div>
            <CardTitle className="text-2xl font-bold bg-gradient-to-r from-red-600 to-orange-600 bg-clip-text text-transparent">
              Reset Your Password
            </CardTitle>
            <CardDescription className="text-gray-600">
              Enter your new password below
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <form onSubmit={handlePasswordReset} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="newPassword" className="text-sm font-medium text-gray-700">New Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    id="newPassword"
                    name="newPassword"
                    type="password"
                    placeholder="Enter new password"
                    value={formData.newPassword}
                    onChange={(e) => setFormData(prev => ({ ...prev, newPassword: e.target.value }))}
                    className="pl-10 h-12 border-gray-200 focus:border-red-500 focus:ring-red-500"
                    required
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-sm font-medium text-gray-700">Confirm Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    placeholder="Confirm new password"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                    className="pl-10 h-12 border-gray-200 focus:border-red-500 focus:ring-red-500"
                    required
                  />
                </div>
              </div>
              
              <Button 
                type="submit" 
                className="w-full h-12 bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white font-semibold rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg" 
                disabled={loading}
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Updating...
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    Update Password
                    <ArrowRight className="w-4 h-4" />
                  </div>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (showForgotPassword) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-orange-50 to-yellow-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 opacity-30" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ff6b6b' fill-opacity='0.03'%3E%3Cpath d='m36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }}></div>
        
        <Card className="w-full max-w-md relative z-10 shadow-2xl border-0 bg-white/90 backdrop-blur-sm">
          <CardHeader className="text-center space-y-4 pb-8">
            <div className="mx-auto w-20 h-20 rounded-full flex items-center justify-center mb-4 overflow-hidden bg-white shadow-lg ring-4 ring-red-100">
              <img 
                src="/lovable-uploads/917e51ff-8660-4c82-b344-b93189a34ba9.png" 
                alt="GAMBGO Logo" 
                className="w-16 h-16 object-contain"
              />
            </div>
            <CardTitle className="text-2xl font-bold bg-gradient-to-r from-red-600 to-orange-600 bg-clip-text text-transparent">
              Reset Password
            </CardTitle>
            <CardDescription className="text-gray-600">
              Enter your email to receive a reset link
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <form onSubmit={handleForgotPassword} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-gray-700">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="Enter your email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    className="pl-10 h-12 border-gray-200 focus:border-red-500 focus:ring-red-500"
                    required
                  />
                </div>
              </div>
              
              <Button 
                type="submit" 
                className="w-full h-12 bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white font-semibold rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg" 
                disabled={loading}
              >
                {loading ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Sending...
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    Send Reset Link
                    <ArrowRight className="w-4 h-4" />
                  </div>
                )}
              </Button>
              
              <Button
                type="button"
                variant="ghost"
                className="w-full h-12 text-gray-600 hover:text-gray-800 hover:bg-gray-100"
                onClick={() => setShowForgotPassword(false)}
              >
                Back to Login
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Avoid the "logged out" flash on app reopen: if a Supabase session is
  // being restored from storage (or already restored), show a branded splash
  // instead of the auth form while we redirect the user into the app.
  const hasStoredSession = (() => {
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith('sb-') && k.endsWith('-auth-token')) {
          const v = localStorage.getItem(k);
          if (v && v.includes('access_token')) return true;
        }
      }
    } catch {}
    return false;
  })();

  if (user || (authLoading && hasStoredSession)) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center">
        <div className="text-center flex-grow flex flex-col justify-center">
          <div className="mb-8">
            <img
              src="/lovable-uploads/40013902-21dc-443e-b648-06fc550f98e5.png"
              alt="GAMBGO Logo"
              className="w-64 h-64 mx-auto object-contain"
            />
          </div>
          <p className="text-2xl font-semibold text-red-600 mb-8">Delivering happiness</p>
        </div>
        <div className="mb-8 text-gray-500 text-sm">
          powered by gambgo express
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-orange-50 to-yellow-50 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-30" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ff6b6b' fill-opacity='0.03'%3E%3Cpath d='m36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
      }}></div>
      
      {/* Floating Elements */}
      <div className="absolute top-20 left-20 w-32 h-32 bg-gradient-to-r from-red-400/20 to-orange-400/20 rounded-full blur-xl animate-pulse"></div>
      <div className="absolute bottom-20 right-20 w-40 h-40 bg-gradient-to-r from-orange-400/20 to-yellow-400/20 rounded-full blur-xl animate-pulse delay-1000"></div>
      
      <Card className="w-full max-w-md relative z-10 shadow-2xl border-0 bg-white/90 backdrop-blur-sm animate-fade-in">
        <CardHeader className="text-center space-y-4 pb-8">
          {/* Logo */}
          <div className="mx-auto w-24 h-24 rounded-full flex items-center justify-center mb-4 overflow-hidden bg-white shadow-lg ring-4 ring-red-100">
            <img 
              src="/lovable-uploads/917e51ff-8660-4c82-b344-b93189a34ba9.png" 
              alt="GAMBGO Logo" 
              className="w-20 h-20 object-contain"
            />
          </div>
          
          <CardTitle className="text-3xl font-bold bg-gradient-to-r from-red-600 to-orange-600 bg-clip-text text-transparent">
            Welcome to GAMBGO
          </CardTitle>
          <CardDescription className="text-gray-600 text-lg">
            delivering happiness
          </CardDescription>
          
          {/* Language Selection */}
          <div className="mt-6">
            <div className="flex items-center gap-2 mb-2">
              <Globe className="w-4 h-4 text-gray-500" />
              <span className="text-sm text-gray-600">Select Language</span>
            </div>
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger className="w-full h-12 border-gray-200 focus:border-red-500 focus:ring-red-500">
                <SelectValue placeholder="Select Language" />
              </SelectTrigger>
              <SelectContent>
                {languageOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {/* Tab Navigation */}
          <div className="flex mb-8 bg-gray-100 rounded-xl p-1.5 shadow-inner">
            <button
              className={`flex-1 py-3 px-6 rounded-lg text-sm font-semibold transition-all duration-200 ${
                activeTab === 'login'
                  ? 'bg-white text-gray-900 shadow-md transform scale-105'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
              onClick={() => setActiveTab('login')}
            >
              Login
            </button>
            <button
              className={`flex-1 py-3 px-6 rounded-lg text-sm font-semibold transition-all duration-200 ${
                activeTab === 'signup'
                  ? 'bg-white text-gray-900 shadow-md transform scale-105'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
              onClick={() => setActiveTab('signup')}
            >
              Sign Up
            </button>
          </div>

          {activeTab === 'login' ? (
            <div className="space-y-6">
              <form onSubmit={handleSignIn} className="space-y-6">
                {!useEmail ? (
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-sm font-medium text-gray-700">Phone Number</Label>
                    <div className="flex rounded-lg border border-gray-200 focus-within:border-red-500 focus-within:ring-2 focus-within:ring-red-500/20 transition-all">
                      <div className="flex items-center bg-gray-50 border-r border-gray-200 px-4 rounded-l-lg">
                        <Phone className="w-4 h-4 text-gray-400 mr-2" />
                        <span className="text-sm text-gray-600 font-medium">+220</span>
                      </div>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        placeholder="7012345"
                        value={formData.phone}
                        onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                        className="border-0 rounded-l-none focus:ring-0 h-12"
                        required
                      />
                    </div>
                    <button
                      type="button"
                      className="text-sm text-red-600 hover:text-red-700 hover:underline font-medium"
                      onClick={() => setUseEmail(true)}
                    >
                      use email instead
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-sm font-medium text-gray-700">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        placeholder="Enter your email"
                        value={formData.email}
                        onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                        className="pl-10 h-12 border-gray-200 focus:border-red-500 focus:ring-red-500"
                        required
                      />
                    </div>
                    <button
                      type="button"
                      className="text-sm text-red-600 hover:text-red-700 hover:underline font-medium"
                      onClick={() => setUseEmail(false)}
                    >
                      use phone instead
                    </button>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-sm font-medium text-gray-700">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      value={formData.password}
                      onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                      className="pl-10 pr-12 h-12 border-gray-200 focus:border-red-500 focus:ring-red-500"
                      required
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 h-8 w-8 p-0 hover:bg-gray-100"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4 text-gray-500" /> : <Eye className="h-4 w-4 text-gray-500" />}
                    </Button>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full h-12 bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white font-semibold rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg" 
                  disabled={loading}
                >
                  {loading ? (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Continuing...
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      Continue
                      <ArrowRight className="w-4 h-4" />
                    </div>
                  )}
                </Button>

                <div className="space-y-3">
                  <Button
                    type="button"
                    variant="ghost"
                    className="w-full h-12 text-gray-600 hover:text-gray-800 hover:bg-gray-100 font-medium"
                    onClick={() => setShowForgotPassword(true)}
                  >
                    Forgot Password?
                  </Button>

                  <Button
                    type="button"
                    variant="outline"
                    className="w-full h-12 border-gray-200 hover:bg-gray-50 font-medium"
                    onClick={handleBrowseAsGuest}
                  >
                    Browse as Guest
                  </Button>
                </div>

                <p className="text-xs text-gray-500 text-center leading-relaxed px-4">
                  By tapping "Continue", you agree to GAMBGO's terms and privacy policy. 
                  We may text you a verification code. Msg & data rates apply.
                </p>
              </form>
            </div>
          ) : (
            <div className="space-y-6">
              <form onSubmit={handleSignUp} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="fullName" className="text-sm font-medium text-gray-700">Full Name</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <Input
                      id="fullName"
                      name="fullName"
                      type="text"
                      placeholder="Enter your full name"
                      value={formData.fullName}
                      onChange={(e) => setFormData(prev => ({ ...prev, fullName: e.target.value }))}
                      className="pl-10 h-12 border-gray-200 focus:border-red-500 focus:ring-red-500"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium text-gray-700">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="Enter your email"
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      className="pl-10 h-12 border-gray-200 focus:border-red-500 focus:ring-red-500"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-sm font-medium text-gray-700">Phone Number</Label>
                  <div className="flex rounded-lg border border-gray-200 focus-within:border-red-500 focus-within:ring-2 focus-within:ring-red-500/20 transition-all">
                    <div className="flex items-center bg-gray-50 border-r border-gray-200 px-4 rounded-l-lg">
                      <Phone className="w-4 h-4 text-gray-400 mr-2" />
                      <span className="text-sm text-gray-600 font-medium">+220</span>
                    </div>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      placeholder="7012345"
                      value={formData.phone}
                      onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                      className="border-0 rounded-l-none focus:ring-0 h-12"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-sm font-medium text-gray-700">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Create a password"
                      value={formData.password}
                      onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                      className="pl-10 pr-12 h-12 border-gray-200 focus:border-red-500 focus:ring-red-500"
                      required
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 h-8 w-8 p-0 hover:bg-gray-100"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4 text-gray-500" /> : <Eye className="h-4 w-4 text-gray-500" />}
                    </Button>
                  </div>
                </div>

                {/* Referral code field */}
                <div>
                  <button
                    type="button"
                    onClick={() => setShowReferralField(!showReferralField)}
                    className="flex items-center gap-2 text-sm text-red-600 hover:text-red-700 font-medium"
                  >
                    <Gift className="w-4 h-4" />
                    {showReferralField ? 'Hide referral code' : 'Have a referral code? (optional)'}
                  </button>
                  {showReferralField && (
                    <div className="mt-2">
                      <input
                        type="text"
                        placeholder="e.g. OMARH3Z"
                        value={formData.referralCode}
                        onChange={(e) => setFormData(prev => ({ ...prev, referralCode: e.target.value.toUpperCase() }))}
                        className="w-full h-12 px-4 border border-gray-200 rounded-lg text-sm font-mono tracking-widest uppercase focus:outline-none focus:border-red-500 focus:ring-2 focus:ring-red-500/20 transition-all"
                      />
                    </div>
                  )}
                </div>

                <p className="text-xs text-gray-500 text-center leading-relaxed px-4">
                  By tapping "Sign Up", you agree to GAMBGO's terms and privacy policy. 
                  We may text you a verification code. Msg & data rates apply.
                </p>

                <Button 
                  type="submit" 
                  className="w-full h-12 bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white font-semibold rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg" 
                  disabled={loading}
                >
                  {loading ? (
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Creating account...
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      Sign Up
                      <ArrowRight className="w-4 h-4" />
                    </div>
                  )}
                </Button>
              </form>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AuthPage;
